// ════════════════════════════════════════════════════════════════════════
// Centralised printer routing for the restaurant POS.
//
// One place that owns the rule: Department → mapped Printer Name → print.
// A department's printer comes from Department Master (Departmentmaster.
// Printername, via /Home/GetDepartments). The BILL printer comes from a
// billing-type department's mapping, or this machine's configured restaurant
// bill printer (PrinterSettings.Restbillprinter, via /Service/getprinter).
//
// The department NAME is NEVER the intended printer — it's only a last-resort
// fallback so a missing mapping can't silently break an existing KOT flow.
//
// Used by: CreateOrder, TableOrders, Order, KotReport — for New / Edit /
// Reprint KOT and Print Bill — so every print routes the same way.
// ════════════════════════════════════════════════════════════════════════
import { useEffect, useState, useCallback } from 'react';
import config from '../../config';

// Department names treated as the billing / front-desk department. The first
// one that exists in Department Master (and has a mapped printer) routes bills.
const BILLING_DEPT_HINTS = [
  'billing',
  'bill',
  'reception',
  'front',
  'counter',
  'cashier',
];

const norm = (s) => (s ?? '').toString().trim().toLowerCase();

// Build a { department-name(lower) → printer name } map from the
// /Home/GetDepartments response (handles PascalCase + camelCase).
export function buildDeptPrinterMap(list) {
  const map = {};
  (Array.isArray(list) ? list : []).forEach((d) => {
    const name = norm(d.Department ?? d.department);
    const printer = (d.Printername ?? d.printername ?? '').toString().trim();
    if (name && printer) map[name] = printer;
  });
  return map;
}

// Resolve a department's mapped printer. Returns '' when not configured.
export function resolveDeptPrinter(map, deptName) {
  if (!map) return '';
  return map[norm(deptName)] || '';
}

// Pick the bill printer from the raw Department list:
//   1) the department explicitly flagged IsBillPrinter (chosen on the
//      Department screen) — its printer,
//   2) else a billing-type department matched by name,
//   3) else this machine's configured Restbillprinter.
// Returns '' when none is configured.
export function pickBillPrinter(deptList, printerCfg) {
  const list = Array.isArray(deptList) ? deptList : [];
  const printerOf = (d) =>
    (d?.Printername ?? d?.printername ?? '').toString().trim();

  // 1) Explicitly flagged bill-printer department.
  const flagged = list.find((d) => (d?.IsBillPrinter ?? d?.isBillPrinter) === true);
  if (flagged && printerOf(flagged)) return printerOf(flagged);

  // 2) A billing-type department by name.
  const byName = list.find((d) => {
    const n = norm(d?.Department ?? d?.department);
    return n && BILLING_DEPT_HINTS.some((h) => n.includes(h));
  });
  if (byName && printerOf(byName)) return printerOf(byName);

  // 3) Machine's configured restaurant bill printer.
  return (printerCfg?.restbillprinter ?? printerCfg?.Restbillprinter ?? '')
    .toString()
    .trim();
}

// React hook — loads the Department→Printer map + the bill printer ONCE and
// exposes resolvers. Reads company/token from sessionStorage so callers don't
// repeat it. Never throws; on any failure the resolvers fall back gracefully.
export function usePrinterRouting() {
  const [deptPrinters, setDeptPrinters] = useState({});
  const [billPrinter, setBillPrinter] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const company = JSON.parse(
      sessionStorage.getItem('companyDetails') || '{}',
    );
    const token = sessionStorage.getItem('token');
    if (!company?.id) {
      setLoaded(true);
      return;
    }
    const machineId = window.location.hostname;
    let alive = true;

    Promise.all([
      fetch(`${config.API_BASE_URL}/Home/GetDepartments/${company.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => (r.ok ? r.json() : []))
        .catch(() => []),
      fetch(
        `${config.API_BASE_URL}/Service/getprinter?companyId=${company.id}&machineId=${encodeURIComponent(machineId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
    ]).then(([deptList, printerCfg]) => {
      if (!alive) return;
      setDeptPrinters(buildDeptPrinterMap(deptList));
      // Pass the RAW list so the IsBillPrinter flag can be read.
      setBillPrinter(pickBillPrinter(deptList, printerCfg));
      setLoaded(true);
    });

    return () => {
      alive = false;
    };
  }, []);

  // KOT printer for a department. Prefer a printer already stamped on the item
  // (opts.itemPrinter, set by the API) → the department's mapping → a single
  // generic fallback. The department NAME is NEVER returned as a printer (that
  // was the original bug); an unmapped department falls back to opts.fallback
  // (default 'Kitchen') so the ticket still prints somewhere instead of being
  // dropped — and a console warning flags the missing mapping.
  const resolveKotPrinter = useCallback(
    (deptName, opts = {}) => {
      const itemPrinter = (opts.itemPrinter ?? '').toString().trim();
      if (itemPrinter) return itemPrinter;
      const mapped = resolveDeptPrinter(deptPrinters, deptName);
      if (mapped) return mapped;
      const d = (deptName ?? '').toString().trim();
      if (d && d.toLowerCase() !== 'general') {
        console.warn(
          `[printer] No printer mapped for department "${d}" — map it in Department Master. Using fallback.`,
        );
      }
      return opts.fallback || 'Kitchen';
    },
    [deptPrinters],
  );

  // BILL printer (billing department mapping or Restbillprinter). Fallback so
  // an unconfigured machine still prints somewhere instead of breaking.
  const resolveBillPrinter = useCallback(
    // #3: was "fallback = 'Kitchen'" — a non-existent BILL printer, so every
    // invoice went to a black hole while the UI toasted success. Return '' when
    // nothing is configured so the caller can raise a real error.
    (fallback = '') => (billPrinter || fallback || '').toString().trim(),
    [billPrinter],
  );

  // True when a real printer is mapped for this department (not a fallback) —
  // callers can use it to warn "Printer not configured for this department".
  const hasDeptPrinter = useCallback(
    (deptName) => !!resolveDeptPrinter(deptPrinters, deptName),
    [deptPrinters],
  );

  return {
    deptPrinters,
    billPrinter,
    loaded,
    resolveKotPrinter,
    resolveBillPrinter,
    hasDeptPrinter,
  };
}
